import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';

import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, filter, firstValueFrom, map, of, shareReplay, Subscription, switchMap, take } from 'rxjs';

import { DateTime } from 'luxon';

import { SelectItem } from 'primeng/api';

import { environment } from '@environment';

import { SiteConfig } from '@shared/models/site-config';

import { fromFormControl } from '@shared/helpers/from-form-control';
import { parseDuration } from '@shared/helpers/parse-time';
import { timeFormat } from '@shared/helpers/timeFormat';
import { WorkedShiftsService } from '@shared/services/worked-shifts.service';
import { CustomerService } from '@shared/services/customer.service';
import { MessageService } from '@shared/services/message-service';
import { Product } from '@shared/models/product';

import { toTimestamp } from '../../../../helpers/to-timestamp';
import { ShiftContextService } from '../../../../services/shift-context.service';
import { InboundLoadService } from '../../../../services/inbound-load.service';
import { ProductService } from '../../../../services/product.service';
import { ShiftContext } from '../../../../models/shift-context';

import { InboundLoad, ProcessInboundLoadUpdate } from '../../../../models/inbound-load';
import { getSiteInfo } from '@shared/helpers/site-info';
import { SubinventoryType } from '@shared/models/subinventory';
import { Dropdown } from 'primeng/dropdown';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

@Component({
    selector: 'app-process-inbound',
    templateUrl: './process-inbound.component.html',
    styleUrls: ['./process-inbound.component.scss']
})
export class ProcessInboundComponent implements OnInit, OnDestroy {
    _load = new BehaviorSubject<InboundLoad>(null);

    @Input() set load(load: InboundLoad) {
        this._load.next(load);
    }

    @ViewChild('newProductId', {static: false}) newProductIdSelect: Dropdown;

    form = new FormGroup({
        unloadedShiftId: new FormControl<number>(null, Validators.required),
        unloadedDate: new FormControl<Date>(null, Validators.required),
        unloadedTime: new FormControl<string>(null, Validators.required),
        processedShiftId: new FormControl<number>(null, Validators.required),
        vendorBolNumber: new FormControl<string>(''),
        trailerId: new FormControl<number>(null),
        isRejected: new FormControl<boolean>(false),
        rejectedBolNumber: new FormControl<string>(''),
        redTagFlag: new FormControl<boolean>(false),
        redTagReason: new FormControl<string>(''),
        inboundLoadLineItems: new FormArray<FormGroup<{
            id: FormControl<number | null>,
            productId: FormControl<number>,
            quantity: FormControl<number>,
            palletDescription: FormControl<string>
        }>>([], (list: FormArray) => {
            return list?.length > 0 ? null : { lineItemsExist: { valid: false } };
        }
)
    });

    newLineForm = new FormGroup({
        productId: new FormControl<number>(null, { validators: Validators.required }),
        quantity: new FormControl<number>(0, [Validators.required, Validators.min(1)]),
        palletDescription: new FormControl<string>('')
    });

    products: BehaviorSubject<Product[]> = new BehaviorSubject<Product[]>([]);

    productSelects = new BehaviorSubject<SelectItem<number | null>[]>([]);

    itemsTotal = fromFormControl(this.form.controls.inboundLoadLineItems).pipe(map(items => {
        return items.reduce((total, item) => {
            return total + (item.quantity ?? 0);
        }, 0);
    }));

    productSubscriptions: Subscription[] = [];
    subscriptions: Subscription[] = [];

    showFullWorkflow: boolean = false;

    supplierBOLRequired: boolean = false;
    requestingBolNumber: boolean = false;
    updating: boolean = false;

    siteTimezone: string = environment.fallbackTimeZone;
    currentContext: ShiftContext;

    constructor(
        private dialogRef: DynamicDialogRef,
        private config: DynamicDialogConfig,
        private inboundLoadService: InboundLoadService,
        private shiftContextService: ShiftContextService,
        private productService: ProductService,
        private workedShiftService: WorkedShiftsService,
        private customerService: CustomerService,
        private messageService: MessageService
    ) {
        this.form.setAsyncValidators([
            async (form: typeof this.form) => {
                if (!this._load.value?.arrivedTimestamp?.isValid)
                    return null;

                var unloadDateTime = DateTime.fromJSDate(form.controls.unloadedDate.value)
                    .plus(parseDuration(form.controls.unloadedTime.value ?? '00:00'));

                var arrivedDateTime = this._load.value.arrivedTimestamp;

                return arrivedDateTime > unloadDateTime
                    ? { timeSequence: { valid: false } }
                    : null;
            },
            async (form: typeof this.form) => {
                const shiftId = form.controls.unloadedShiftId.value;

                if (!shiftId)
                    return null;

                var unloadDate = DateTime.fromJSDate(form.controls.unloadedDate.value);

                var shiftDate = await firstValueFrom(this.workedShiftService.PeekById(shiftId));

                const diff = unloadDate.diff(shiftDate.date, 'days').days;

                return diff > 1 || diff < 0
                    ? { datediff: { valid: false } }
                    : null;
            },
            async (form: typeof this.form) => {
                if (!form.controls.unloadedShiftId.value || !form.controls.processedShiftId.value)
                    return null;

                const unloadShift = await firstValueFrom(this.workedShiftService.PeekById(form.controls.unloadedShiftId.value));
                const processShift = await firstValueFrom(this.workedShiftService.PeekById(form.controls.processedShiftId.value));

                return unloadShift.date > processShift.date
                    ? { shiftSequence: { valid: false } }
                    : null;
            }
        ]);
    }

    ngOnInit(): void {
        this._load.next(this.config.data?.load ?? null);
        const customerAssignments = combineLatest([
            this.shiftContextService.CurrentContext,
            fromFormControl(this.form.controls.processedShiftId).pipe(
                switchMap(shiftId => shiftId ? this.workedShiftService.FollowById(shiftId) : of(null)),
                map(shift => shift?.date),
                filter(date => !!date),
                distinctUntilChanged((prev, curr) => prev?.equals(curr) ?? false))
        ]).pipe(
            switchMap(([context, processedShiftDate]) => this.customerService.getCustomers(context.site.id, processedShiftDate, 'inbound')),
            shareReplay(1));

        this.subscriptions = [
            this.shiftContextService.SiteConfig.subscribe(configs => {
                this.siteTimezone = configs
                    .find(c => c.configKey === SiteConfig.SITE_TIME_ZONE)
                    ?.GetValue<string>()
                    ?? environment.fallbackTimeZone;
            }),

            this.shiftContextService.CurrentContext.subscribe(context => {
                this.currentContext = context;
            }),

            this.productService.CurrentProducts.subscribe(products => {
                this.products.next(products);
            }),

            combineLatest([
                this.shiftContextService.CurrentContext,
                this._load.pipe(filter(l => !!l))
            ]).subscribe(([context, load]) => {
                const subinv = context.site.subinventories.find(s => s.id === load.siteSubinventoryId);
                this.showFullWorkflow = [
                    SubinventoryType.PecoPallets,
                    SubinventoryType.PecoComponents,
                    SubinventoryType.IgpsPallets
                ].includes(subinv?.subinventoryType);
            }),

            fromFormControl(this.form.controls.unloadedShiftId).subscribe(shiftId => {
                if (!this.showFullWorkflow)
                    this.form.controls.processedShiftId.setValue(shiftId);
            }),

            combineLatest([this._load, customerAssignments]).subscribe(([load, customerAssignments]) => {
                const customer = customerAssignments.customers.find(s => s.id === load.customerId);

                if (customer) {
                    this.form.controls.vendorBolNumber.clearValidators();

                    this.supplierBOLRequired = customer.isCustomerRefRequired;

                    if (this.supplierBOLRequired)
                        this.form.controls.vendorBolNumber.setValidators(Validators.required);

                    this.form.controls.vendorBolNumber.updateValueAndValidity();
                }
            }),

            combineLatest([
                this._load.pipe(filter(load => !!load)),
                this.products,
                customerAssignments
            ])
                .pipe(debounceTime(100))
                .subscribe(([ load, products, assignments ]) => {
                    const productIds = assignments?.assignments
                        .filter(a => a.customerId == load.customerId
                            && a.siteSubinventoryId == load.siteSubinventoryId
                            && a.poolerId == load.poolerId
                            && (a.poolerSiteId == load.customerSiteId || a.poolerSiteId == null))
                        .flatMap(a => a.productIds)
                        ?? [];

                    this.productSelects.next([
                        { value: null, label: '-- Select Product --'},
                        ...products.filter(p => productIds.includes(p.id)).map(p => ({value: p.id, label: p.label}))
                    ]);
                }),

            fromFormControl(this.newLineForm.controls.productId).subscribe(productId => {
                const product = this.products.value.find(p => p.id === productId);

                if (product?.itemDescription === 'Merchandise'){
                    this.newLineForm.controls.palletDescription.enable();
                    this.newLineForm.controls.palletDescription.setValidators([Validators.required]);
                }
                else {
                    this.newLineForm.controls.palletDescription.disable();
                    this.newLineForm.controls.palletDescription.clearValidators();
                }

                this.newLineForm.controls.quantity.clearValidators();
                this.newLineForm.controls.quantity.setValidators([
                    Validators.required,
                    Validators.min(0),
                    Validators.max(product?.maxLoadQuantity ?? Infinity)
                ]);
            }),

            this._load
                .pipe(switchMap(load => combineLatest([
                    of(load),
                    this.workedShiftService.PeekById(load?.unloadedWorkedShiftId ?? this.currentContext?.id)
                        .pipe(take(1))
                ])))
                .subscribe(([load, initShift]) => {
                    if (load) {
                        this.form.controls.inboundLoadLineItems.clear();
                        this.productSubscriptions.forEach(s => s.unsubscribe());
                        this.productSubscriptions = [];

                        this.form.setValue({
                            unloadedShiftId: load.unloadedWorkedShiftId ?? initShift?.id ?? null,
                            processedShiftId: initShift?.id ?? null,
                            unloadedDate: (load.unloadedTimestamp ?? initShift.date).startOf('day').toJSDate(),
                            unloadedTime: timeFormat((load.unloadedTimestamp ?? DateTime.local()).setZone(this.siteTimezone)),
                            vendorBolNumber: load.vendorBolNumber ?? '',
                            trailerId: load.trailerId ?? null,
                            isRejected: load.isRejected,
                            rejectedBolNumber: load.rejectedBolNumber ?? '',
                            redTagFlag: load.redTagFlag,
                            redTagReason: load.redTagReason ?? '',
                            inboundLoadLineItems: []
                        });

                        for(const item of load.inboundLoadLineItems) {
                            const itemForm = new FormGroup({
                                id: new FormControl<number | null>(item.id),
                                productId: new FormControl<number>(item.productId, Validators.required),
                                quantity: new FormControl<number>(item.quantity, Validators.required),
                                palletDescription: new FormControl<string>(item.palletDescription ?? '')
                            });

                            this.form.controls.inboundLoadLineItems.push(itemForm);

                            this.productSubscriptions.push(fromFormControl(itemForm.controls.productId).subscribe(productId => {
                                const product = this.products.value.find(p => p.id === productId);

                                if (product.itemDescription === 'Merchandise'){
                                    itemForm.controls.palletDescription.enable();
                                    itemForm.controls.palletDescription.setValidators([Validators.required]);
                                }
                                else {
                                    itemForm.controls.palletDescription.disable();
                                    itemForm.controls.palletDescription.clearValidators();
                                }

                                itemForm.controls.quantity.clearValidators();
                                itemForm.controls.quantity.setValidators([
                                    Validators.required,
                                    Validators.min(0),
                                    Validators.max(product.maxLoadQuantity)
                                ]);
                            }));
                        }

                        this.form.updateValueAndValidity();
                    }
                    else {
                        this.form.reset();
                    }
                })
        ];
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    getBolNumber(): void {
        this.requestingBolNumber = true;

        if (this.currentContext) {
            this.inboundLoadService.getNextBolNumber(this.currentContext.site.id).subscribe({
                next: bolNum => {
                    this.form.controls.vendorBolNumber.setValue(bolNum);
                    this.requestingBolNumber = false;
                },
                error: _err => {

                    this.requestingBolNumber = false;
                }
            });
        }
    }

    addLineItem(): void {
        if (this.newLineForm.invalid) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Could not Add Line Item',
                detail: 'Please correct the listed errors.'
            });
            this.newLineForm.markAllAsTouched();
            return;
        }

        const newItem = new FormGroup({
            id: new FormControl<number | null>(null),
            productId: new FormControl<number>(this.newLineForm.controls.productId.value, Validators.required),
            quantity: new FormControl<number>(this.newLineForm.controls.quantity.value, Validators.required),
            palletDescription: new FormControl<string>(this.newLineForm.controls.palletDescription.value ?? '')
        });

        this.productSubscriptions.push(fromFormControl(newItem.controls.productId).subscribe(productId => {
            const product = this.products.value.find(p => p.id === productId);

            if (product.itemDescription === 'Merchandise'){
                newItem.controls.palletDescription.enable();
                newItem.controls.palletDescription.setValidators([Validators.required]);
            }
            else {
                newItem.controls.palletDescription.disable();
                newItem.controls.palletDescription.clearValidators();
            }

            newItem.controls.quantity.clearValidators();
            newItem.controls.quantity.setValidators([
                Validators.required,
                Validators.min(0),
                Validators.max(product.maxLoadQuantity)
            ]);
        }));

        this.form.controls.inboundLoadLineItems.push(newItem);

        this.newLineForm.reset();
        this.newProductIdSelect?.focus();
    }

    deleteLineItem(index: number): void {
        this.form.controls.inboundLoadLineItems.removeAt(index);
    }

    processLoad(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();

            this.messageService.add({
                severity: 'warn',
                summary: 'Could not Unload Load',
                detail: 'Please correct the listed errors.'
            });

            return;
        }

        const update: ProcessInboundLoadUpdate = {
            unloadedWorkedShiftId: this.form.controls.unloadedShiftId.value,
            unloadedTimestamp: toTimestamp(this.form.controls.unloadedDate.value, this.form.controls.unloadedTime.value, this.siteTimezone),
            processedWorkedShiftId: this.form.controls.processedShiftId.value,
            vendorBolNumber: this.form.controls.vendorBolNumber.value,
            trailerId: this.form.controls.trailerId.value,
            isRejected: this.form.controls.isRejected.value,
            rejectedBolNumber: this.form.controls.rejectedBolNumber.value,
            redTagFlag: this.form.controls.redTagFlag.value,
            redTagReason: this.form.controls.redTagReason.value,
            inboundLoadLineItems: this.form.controls.inboundLoadLineItems.value.map(item => ({
                id: item.id ?? 0,
                productId: item.productId,
                quantity: item.quantity,
                palletDescription: item.palletDescription
            }))
        };

        this.updating = true;
        this.inboundLoadService.processInboundLoad(this._load.value.id, update)
            .subscribe({
                next: (load) => {
                    this.updating = false;
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Load Unloaded',
                        detail: `Load ${this._load.value.vendorBolNumber} has been unloaded.`
                    });

                    this.dialogRef.close(load);
                },
                error: _ => {
                    this.updating = false;
                }
            });
    }
}
